/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import Button from '@react/react-spectrum/Button';
import RadioGroup from '@react/react-spectrum/RadioGroup';
import Radio from '@react/react-spectrum/Radio';
import '@react/react-spectrum/Textfield';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import metadata from 'libphonenumber-js/metadata.min.json';
import { getCountryCallingCode } from 'libphonenumber-js/core';
import CountrySelect from './countrySelect';
import { addClass } from '../../utils/cssUtils';
import { interpretKey } from '../../utils/eventUtil';
import TranslationUtil from '../../utils/translation';
import './sendLink.scss';
import { getOSDisplayString } from '../../utils/commonUtil';

const TEXT_FIELD_VALIDATION = {
    VALID: 'valid',
    INVALID: 'invalid',
};
const SPECTRUM_TEXTFIELD_CLASS = 'spectrum-Textfield';
const PHONE_INPUT_CLASS_NAME = 'react-phone-number-input__input';
const SUB_TEXT_MESSAGE_ID = {
    INVALID_NUM: 'ccx.send-link.sms.invalid-number',
    VALID_NUM: 'ccx.send-link.sms.valid-number',
    NUM_MATCH: 'ccx.send-link.sms.number-mismatch',
    NUM_MISMATCH: 'ccx.send-link.sms.number-match',
};

const DEFAULT_COUNTRY = 'US';

class SendLinkMobileDevice extends Component {
    constructor(props) {
        super(props);
        const { data: { supportedServices, adobeIDEmailAddresses } } = props;
        this.state = {
            selectedService: supportedServices[0],
            smsPhoneNum: '',
            smsPhoneNumMsgId: '',
            smsConfirmNum: '',
            smsConfirmNumMsgId: '',
            showConfirmNumBox: false,
            email: adobeIDEmailAddresses ? adobeIDEmailAddresses[0] : '',
            isSendLinkInProgress: false,
        };
    }

    componentWillMount() {
        this.country = this.getInitialCountry();
    }

    componentDidMount() {
        const phoneInputCompts = document.getElementsByClassName(PHONE_INPUT_CLASS_NAME);
        addClass(phoneInputCompts[0], SPECTRUM_TEXTFIELD_CLASS);
        addClass(phoneInputCompts[1], SPECTRUM_TEXTFIELD_CLASS);
        this.updatePhoneNumberField(this.country);
    }

    componentDidUpdate() {
        const phoneInputCompts = document.getElementsByClassName(PHONE_INPUT_CLASS_NAME);
        addClass(phoneInputCompts[0], SPECTRUM_TEXTFIELD_CLASS);
        addClass(phoneInputCompts[1], SPECTRUM_TEXTFIELD_CLASS);
        this.updatePhoneNumberField(this.country);
    }

    onServiceSelect(selectedService) {
        this.setState({ selectedService });
        this.onSMSPhoneNumUpdate();
        this.onSMSConfirmNumUpdate();
    }

    onSMSPhoneNumChange(smsPhoneNum) {
        this.setState({ smsPhoneNum }, () => { this.onSMSPhoneNumUpdate(); });
    }

    onSMSConfirmNumChange(smsConfirmNum) {
        this.setState({ smsConfirmNum }, () => { this.onSMSConfirmNumUpdate(); });
    }

    onSMSPhoneNumUpdate() {
        const { smsPhoneNum } = this.state;
        if (smsPhoneNum && !isValidPhoneNumber(smsPhoneNum)) {
            this.setState({
                smsPhoneNumMsgId: SUB_TEXT_MESSAGE_ID.INVALID_NUM,
                smsConfirmNum: '',
                showConfirmNumBox: false,
            });
        } else if (smsPhoneNum) {
            this.setState({
                smsPhoneNumMsgId: SUB_TEXT_MESSAGE_ID.VALID_NUM,
                showConfirmNumBox: true,
            }, () => {
                this.updateConfirmNumberField(this.country);
            });
        }
    }

    onSMSConfirmNumUpdate() {
        const { smsPhoneNum, smsConfirmNum } = this.state;
        if (smsConfirmNum && smsConfirmNum !== smsPhoneNum) {
            this.setState({ smsConfirmNumMsgId: SUB_TEXT_MESSAGE_ID.NUM_MATCH });
        } else {
            this.setState({ smsConfirmNumMsgId: SUB_TEXT_MESSAGE_ID.NUM_MISMATCH });
        }
    }

    onEmailSelect(email) {
        this.setState({ email });
    }

    onCountryUpdate(country) {
        this.country = country;
        requestAnimationFrame(() => {
            this.updatePhoneNumberField(country);
            this.updateConfirmNumberField(country);
        });
    }

    onSendLinkKeyDown() {
        return interpretKey({ onEnter: [this.onSendLinkClick.bind(this), true, true] });
    }

    onSendLinkClick(e) {
        e.preventDefault();
        const { data: { app: { sapcode }, onAction } } = this.props;
        const { selectedService, isSendLinkInProgress } = this.state;
        const sendLinkDisabled = (selectedService === 'sms' && this.getPhoneNumberFieldClass() !== TEXT_FIELD_VALIDATION.VALID) || isSendLinkInProgress;
        if (!sendLinkDisabled) {
            this.setIsSendLinkInProgress(true);
            onAction(
                e,
                selectedService,
                sapcode,
                this.setIsSendLinkInProgress.bind(this),
                this.getServiceInput(selectedService),
            );
        }
    }

    getInitialCountry() {
        const { data: { userCountry } } = this.props;
        return userCountry || DEFAULT_COUNTRY;
    }

    getServiceInput(selectedService) {
        const { smsConfirmNum, email } = this.state;
        switch (selectedService) {
        case 'sms':
            return smsConfirmNum;
        case 'email':
            return email;
        default:
            return '';
        }
    }

    getPhoneNumberFieldClass() {
        const { smsPhoneNum, smsConfirmNum } = this.state;
        if (smsConfirmNum && smsConfirmNum === smsPhoneNum) {
            return TEXT_FIELD_VALIDATION.VALID;
        }
        return '';
    }

    getConfirmNumberFieldClass() {
        const { smsPhoneNum, smsConfirmNum } = this.state;
        if (smsConfirmNum) {
            if (smsConfirmNum === smsPhoneNum) {
                return TEXT_FIELD_VALIDATION.VALID;
            }
            return TEXT_FIELD_VALIDATION.INVALID;
        }
        return '';
    }

    setIsSendLinkInProgress(isInProgress) {
        this.setState({ isSendLinkInProgress: isInProgress });
    }

    updatePhoneNumberField(country) {
        if (country) {
            const phoneInputCompts = document.getElementsByClassName(PHONE_INPUT_CLASS_NAME);
            if (phoneInputCompts && phoneInputCompts[0] && !phoneInputCompts[0].value) {
                const callingCode = `+ ${getCountryCallingCode(country, metadata)} `;
                phoneInputCompts[0].value = callingCode;
            }
        }
    }

    updateConfirmNumberField(country) {
        if (country) {
            const phoneInputCompts = document.getElementsByClassName(PHONE_INPUT_CLASS_NAME);
            if (phoneInputCompts && phoneInputCompts[1] && !phoneInputCompts[1].value) {
                const callingCode = `+ ${getCountryCallingCode(country, metadata)} `;
                phoneInputCompts[1].value = callingCode;
            }
        }
    }

    renderSMSServiceContent() {
        const {
            smsPhoneNumMsgId,
            smsConfirmNumMsgId,
            smsPhoneNum,
            smsConfirmNum,
            showConfirmNumBox,
        } = this.state;
        return (
            <div className="sms-content">
                <div className="description">
                    {TranslationUtil.getMessage('ccx.send-link.sms.description')}
                </div>
                <div className="phone-input">
                    <div id="phone-input-primary">
                        {TranslationUtil.getMessage('ccx.send-link.sms.phone-number')}
                        <PhoneInput
                            onCopy={(e) => { e.preventDefault(); }}
                            onCut={(e) => { e.preventDefault(); }}
                            onPaste={(e) => { e.preventDefault(); }}
                            autocomplete="off"
                            country={this.country}
                            className={this.getPhoneNumberFieldClass()}
                            countrySelectComponent={CountrySelect}
                            onCountryChange={this.onCountryUpdate.bind(this)}
                            value={smsPhoneNum}
                            onChange={this.onSMSPhoneNumChange.bind(this)} />
                        {smsPhoneNumMsgId && (
                            <div className="number-invalid">
                                { TranslationUtil.getMessage(smsPhoneNumMsgId) }
                            </div>
                        )}
                    </div>
                    {showConfirmNumBox
                        && (
                            <div id="phone-input-confirm">
                                {TranslationUtil.getMessage('ccx.send-link.sms.confirm-number')}
                                <PhoneInput
                                    onCopy={(e) => { e.preventDefault(); }}
                                    onCut={(e) => { e.preventDefault(); }}
                                    onPaste={(e) => { e.preventDefault(); }}
                                    autocomplete="off"
                                    className={this.getConfirmNumberFieldClass()}
                                    value={smsConfirmNum}
                                    showCountrySelect={false}
                                    onKeyDown={this.onSendLinkKeyDown()}
                                    onChange={this.onSMSConfirmNumChange.bind(this)} />
                                <div className="number-mismatch">
                                    {smsConfirmNumMsgId
                                        && TranslationUtil.getMessage(smsConfirmNumMsgId)}
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>
        );
    }

    renderEmailServiceContent() {
        const {
            data: { adobeIDEmailAddresses },
        } = this.props;
        if (adobeIDEmailAddresses.length > 1) {
            const emailAddButtons = [];
            adobeIDEmailAddresses.forEach((address) => {
                emailAddButtons.push(
                    <Radio
                        key={address}
                        value={address}
                        label={address}
                        role="radio"
                        aria-label="Email"
                        onKeyDown={this.onSendLinkKeyDown()} />
                );
            });
            return (
                <div className="email-content">
                    <div className="description">
                        {TranslationUtil.getMessage('ccx.send-link.email.description.multiple-id')}
                    </div>
                    <RadioGroup
                        role="radiogroup"
                        name="email-addresses"
                        vertical
                        onChange={this.onEmailSelect.bind(this)}
                        defaultSelectedValue={adobeIDEmailAddresses[0]}>
                        {emailAddButtons}
                    </RadioGroup>
                </div>
            );
        }
        return (
            <div className="email-content">
                <div className="description">
                    {TranslationUtil.getMessage('ccx.send-link.email.description.single-id')}
                    <b>{adobeIDEmailAddresses[0]}</b>
                </div>
            </div>
        );
    }

    renderServiceContent() {
        const { selectedService } = this.state;
        switch (selectedService) {
        case 'sms':
            return this.renderSMSServiceContent();
        case 'email':
            return this.renderEmailServiceContent();
        default:
            return null;
        }
    }

    renderServiceOptions(supportedServices) {
        const radioButtons = [];
        supportedServices.forEach((service) => {
            radioButtons.push(
                <Radio key={service} value={service} role="radio" aria-label={`${service}`}>
                    {TranslationUtil.getMessage(`ccx.send-link.service-title.${service}`)}
                </Radio>
            );
        });
        return (
            <div className="send-service">
                <div className="service-options-header">
                    { TranslationUtil.getMessage('ccx.send-link.service-title.send.via') }
                </div>
                <RadioGroup
                    role="radiogroup"
                    className="service-options"
                    name="send-link-services"
                    onChange={this.onServiceSelect.bind(this)}
                    defaultSelectedValue={supportedServices[0]}>
                    {radioButtons}
                </RadioGroup>
            </div>
        );
    }

    renderSupportedOSList(supportedOS) {
        let compatibilityStr = '';
        if (supportedOS && supportedOS.length) {
            // Filter empty entries(if any)
            const filteredSupportedOS = supportedOS.filter(Boolean);
            if (!filteredSupportedOS.length) return null;
            if (filteredSupportedOS.length === 1) {
                compatibilityStr = getOSDisplayString(filteredSupportedOS[0]);
            } else if (filteredSupportedOS.length === 2) {
                compatibilityStr = TranslationUtil.getMessage('ccx.pdp.information.compatibility.supportedOS.two', {
                    firstOS: getOSDisplayString(filteredSupportedOS[0]),
                    secondOS: getOSDisplayString(filteredSupportedOS[1]),
                });
            } else if (filteredSupportedOS.length === 3) {
                compatibilityStr = TranslationUtil.getMessage('ccx.pdp.information.compatibility.supportedOS.three', {
                    firstOS: getOSDisplayString(filteredSupportedOS[0]),
                    secondOS: getOSDisplayString(filteredSupportedOS[1]),
                    thirdOS: getOSDisplayString(filteredSupportedOS[2]),
                });
            } else if (filteredSupportedOS.length === 4) {
                compatibilityStr = TranslationUtil.getMessage('ccx.pdp.information.compatibility.supportedOS.four', {
                    firstOS: getOSDisplayString(filteredSupportedOS[0]),
                    secondOS: getOSDisplayString(filteredSupportedOS[1]),
                    thirdOS: getOSDisplayString(filteredSupportedOS[2]),
                    fourthOS: getOSDisplayString(filteredSupportedOS[3]),
                });
            }

            return (
                <div className="supported-os">
                    <div className="comptibility-header">
                        { TranslationUtil.getMessage('ccx.pdp.information.compatibility.field-name')}
                    </div>
                    <div className="compatibility-os-list">
                        { compatibilityStr }
                    </div>
                </div>
            );
        }
        return null;
    }

    renderFooter() {
        const { data: { app: { sapcode }, onAction } } = this.props;
        const { selectedService, isSendLinkInProgress } = this.state;
        const sendLinkButtonDisabled = (selectedService === 'sms' && this.getPhoneNumberFieldClass() !== TEXT_FIELD_VALIDATION.VALID) || isSendLinkInProgress;
        return [
            <Button
                role="button"
                aria-label="Send Link"
                key="send-link-button"
                className="send-link-button"
                onClick={(e) => { this.onSendLinkClick(e); }}
                onKeyDown={this.onSendLinkKeyDown()}
                disabled={sendLinkButtonDisabled}
                variant="cta">
                {TranslationUtil.getMessage('ccx.send-link.send-link-button')}
            </Button>,
            <Button
                role="button"
                aria-label="Cancel"
                key="cancel-button"
                className="cancel-button"
                onClick={(e) => { onAction(e, 'cancel', sapcode, this.setIsSendLinkInProgress.bind(this)); }}
                disabled={isSendLinkInProgress}
                variant="primary">
                {TranslationUtil.getMessage('ccx.send-link.cancel-button')}
            </Button>,
        ];
    }

    render() {
        const {
            data: {
                app: { supportedOS, appFamilyName }, supportedServices,
            },
        } = this.props;
        return (
            <div className="send-link">
                <div className="header">
                    <div className="title">{TranslationUtil.getMessage('ccx.send-link.dialog-title', { appName: appFamilyName })}</div>
                </div>
                <div className="content">
                    {this.renderSupportedOSList(supportedOS)}
                    {(supportedServices.length > 1) && this.renderServiceOptions(supportedServices)}
                    {this.renderServiceContent()}
                </div>
                <div className="footer">
                    {this.renderFooter()}
                </div>
            </div>
        );
    }
}

export default observer(SendLinkMobileDevice);
